import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { INBOX_NAME_LIMIT } from 'shared/constants/limits'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox/api/inboxes'
import { type UserOwner } from 'entities/Users/model/UserOwner'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { DeleteInboxModalContent } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/general/ui/DeleteInboxModalContent/DeleteInboxModalContent'

export class MessagingGeneralStore {
  private _name = ''
  private _errorMessage = ''
  private _colorCodingEnabled = false
  private _useOrgHistory = true
  private _inboxHistory = false
  private _selectedReassignInbox: IResponseInbox | null = null
  private _modalId = nanoid()
  private _checkboxStates: Record<string, boolean> = {
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
  }
  private _deleteInboxLoading = false
  private _limitReached = false
  private _inbox: IResponseInbox | null = null

  constructor(private _inboxSettingsStore: InboxSettingsStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = (inbox: IResponseInbox) => {
    this._inbox = inbox
    this._name = inbox.name ?? ''
    this._colorCodingEnabled = inbox.color_coding_enabled ?? false
    this._useOrgHistory = inbox.use_global_conversation_history ?? false
    this._inboxHistory = inbox.use_conversation_history ?? true
  }

  get name() {
    return this._name
  }

  get inboxId() {
    return this._inboxSettingsStore.inboxId
  }

  get limit() {
    return INBOX_NAME_LIMIT
  }

  get warningMessage() {
    return this.limitReached ? `Maximum character length is ${this.limit} characters.` : undefined
  }

  get errorMessage() {
    return this._errorMessage
  }

  get colorCodingEnabled() {
    return this._colorCodingEnabled
  }

  get useOrgHistory() {
    return this._useOrgHistory
  }

  get inboxHistory() {
    return this._inboxHistory
  }

  get isInboxHistoryDisabled() {
    return this._useOrgHistory === true
  }

  get availableInboxes() {
    const inboxesViewStore = this._inboxSettingsStore.inboxLayerStore.inboxesViewStore

    return inboxesViewStore.items.filter((inbox) => inbox.id !== this.inboxId)
  }

  get hasOnlyOneInbox() {
    return this.availableInboxes.length === 0
  }

  get availableInboxesLoading() {
    return this._inboxSettingsStore.inboxLayerStore.inboxesViewStore.loading
  }

  get selectedReassignInbox() {
    return this._selectedReassignInbox
  }

  get checkboxStates() {
    return this._checkboxStates
  }

  get allCheckboxesChecked() {
    return Object.values(this._checkboxStates).every((checked) => checked)
  }

  get isDeleteDisabled() {
    return !this.allCheckboxesChecked || !this._selectedReassignInbox
  }

  get entities() {
    return this._inboxSettingsStore.inboxLayerStore.entities
  }

  get currentUser() {
    return this.entities.usersStore.user as UserOwner
  }

  get isCurrentUserInboxOwner() {
    if (!this._inbox) return false
    if (!this.currentUser) return false
    return this._inbox?.owner_id === this.currentUser?.id
  }

  get restrictManageDeleteInbox() {
    if (!checkAccessPage(EnumAccessPageKeys.inbox_page, this.entities)) {
      if (
        this.isCurrentUserInboxOwner &&
        (this.currentUser?.isManagerRole || this.currentUser?.isAdminRole)
      ) {
        return false
      }
      return true
    }

    return this.currentUser?.isMemberRole
  }

  get deleteInboxLoading() {
    return this._deleteInboxLoading
  }

  get limitReached() {
    return this._limitReached
  }

  setLimitReached = (status: boolean) => {
    this._limitReached = status
  }

  setName = (value: string) => {
    this.clearErrorMessage()
    this._name = value
  }

  saveName = async () => {
    if (!this._name) {
      this._errorMessage = 'Inbox name is required.'
      return false
    }
    if (this.limitReached) {
      return false
    }

    this.clearErrorMessage()

    const currentInboxName = this._inboxSettingsStore.inbox?.name
    if (this._name === currentInboxName || !this.inboxId) {
      return false
    }

    try {
      await InboxesApi.updateTeamsById(this.inboxId, { name: this._name })
      toastStore.add({
        type: 'success',
        title: 'Inbox name updated successfully',
      })
      return true
    } catch (e) {
      if (e instanceof AxiosError && e.errors.getError('name')) {
        this._errorMessage = e.errors.getError('name')
      }
      logger.error(e)
      return false
    }
  }

  clearErrorMessage = () => {
    this._errorMessage = ''
  }

  toggleColorCoding = async (value: boolean) => {
    const prev = this._colorCodingEnabled
    this._colorCodingEnabled = value

    if (!this.inboxId) {
      return
    }

    try {
      await InboxesApi.updateTeamByColorCoding(this.inboxId, { enabled: value })
      toastStore.add({
        type: 'success',
        title: value ? 'Color coding activated' : 'Color coding deactivated',
      })
    } catch (e) {
      logger.error(e)
      this._colorCodingEnabled = prev
    }
  }

  toggleUseOrgHistory = (value: boolean) => {
    const newInboxHistory = value ? false : this._inboxHistory
    this._updateHistory(value, newInboxHistory, value ? 'orgOn' : 'orgOff')
  }

  toggleInboxHistory = (value: boolean) => {
    this._updateHistory(false, value, value ? 'inboxOn' : 'inboxOff')
  }

  private _updateHistory = async (
    useOrg: boolean,
    inboxHistory: boolean,
    toastKey: 'orgOn' | 'orgOff' | 'inboxOn' | 'inboxOff'
  ) => {
    const prevOrg = this._useOrgHistory
    const prevInboxHistory = this._inboxHistory

    this._useOrgHistory = useOrg
    this._inboxHistory = inboxHistory

    const params = {
      use_conversation_history: this._inboxHistory,
      use_global_conversation_history: this._useOrgHistory,
    }

    if (!this.inboxId) {
      return
    }

    try {
      await InboxesApi.updateTeamByHistory(this.inboxId, params)
      const titles = {
        orgOn: 'Organization settings enabled',
        orgOff: 'Organization settings disabled',
        inboxOn: 'Inbox conversations history enabled',
        inboxOff: 'Inbox conversations history disabled',
      }

      toastStore.add({ type: 'success', title: titles[toastKey] })
    } catch (e) {
      logger.error(e)
      this._useOrgHistory = prevOrg
      this._inboxHistory = prevInboxHistory
    }
  }

  setSelectedReassignInbox = (inbox: IResponseInbox | null) => {
    this._selectedReassignInbox = inbox
  }

  setCheckboxState = (id: string, checked: boolean) => {
    this._checkboxStates[id] = checked
  }

  resetCheckboxStates = () => {
    this._checkboxStates = {
      checkbox1: false,
      checkbox2: false,
      checkbox3: false,
    }
  }

  removeModal = () => {
    modalStore.removeModal(this._modalId)
  }

  openDeleteInboxModal = () => {
    this.setSelectedReassignInbox(null)
    this.resetCheckboxStates()

    modalStore.addModal({
      id: this._modalId,
      title: 'Delete inbox',
      width: 480,
      showCloseButton: false,
      paddingContent: '0px 24px 22px 24px',
      ModalContentProps: {
        store: this,
      },
      ModalContent: DeleteInboxModalContent,
      disabledOverlayClose: true,
    })
  }

  handleDeleteInbox = async () => {
    if (!this._selectedReassignInbox || !this.inboxId) {
      return
    }

    try {
      this._deleteInboxLoading = true

      await InboxesApi.deleteInbox(this.inboxId, {
        confirm: 1,
        release: 1,
        broadcast: 1,
        reassign: `App\\Teams\\Team:${this._selectedReassignInbox.id}`,
      })
      toastStore.add({
        type: 'success',
        title: `${this._name} inbox deleted`,
      })

      this.removeModal()

      uiStore.changeRoute({ path: `/${InboxesPath.root}` })
    } catch (error) {
      logger.error(error)
      const isTrialRestriction =
        error instanceof AxiosError &&
        error.response?.status === 403 &&
        this._entities.subscriptionStore.isTrial

      if (isTrialRestriction) {
        toastStore.add({
          type: 'error',
          title: 'Inbox deletion isn’t available on the trial plan.',
          desc: 'Upgrade your plan to delete inboxes.',
        })
      } else {
        toastStore.add({
          type: 'error',
          title: 'Failed to delete inbox',
        })
      }
    } finally {
      this._deleteInboxLoading = false
    }
  }
}
